'use client';

import React, { useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Sparkles, MapPin, Heart, Share2, Crown, Loader2, ChevronLeft,
  ToggleLeft, ToggleRight, Lock, Navigation, MessageSquare,
  ChevronDown, ChevronUp, Clock, Tag, ExternalLink, Send,
} from 'lucide-react';

import { Offer } from '@/types';
import SafeImage from '@/components/SafeImage';
import BottomNav from '@/components/BottomNav';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import WinewoodPlusModal from '@/components/WinewoodPlusModal';
import { useRouter } from 'next/navigation';
import InquiryModal from '@/app/offer-detail-page/components/InquiryModal';

interface EpicPageContentProps {
  offers: Offer[];
}

export default function EpicPageContent({ offers }: EpicPageContentProps) {
  const { user, favoriteIds, toggleFavorite: authToggleFavorite, session } = useAuth();
  const router = useRouter();

  // Determine Plus status from subscription or manual_plus (loaded in profile)
  // For now we check if user has a session — real Plus check is done via profile
  const [isPlusUser] = useState<boolean>(false); // Will be overridden by profile check below
  const [plusChecked, setPlusChecked] = useState(false);
  const [isPlus, setIsPlus] = useState(false);
  const [showPlusModal, setShowPlusModal] = useState(false);
  const [plusModalHint, setPlusModalHint] = useState<string | undefined>(undefined);

  // Inquiry modal state
  const [inquiryOffer, setInquiryOffer] = useState<Offer | null>(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [epicUserDisplayName, setEpicUserDisplayName] = useState<string>('');

  // Load plus status once
  React.useEffect(() => {
    if (!user || plusChecked) return;
    setPlusChecked(true);
    import('@/lib/supabase/client').then(({ createClient }) => {
      const sb = createClient();
      sb.from('profiles').select('manual_plus, manual_plus_until, display_name').eq('id', user.id).single()
        .then(({ data }) => {
          if (!data) return;
          if (data.display_name) setEpicUserDisplayName(data.display_name);
          const now = new Date();
          const manualActive =
            data.manual_plus &&
            (!data.manual_plus_until || new Date(data.manual_plus_until) > now);
          if (manualActive) { setIsPlus(true); return; }
          sb.from('subscriptions')
            .select('status')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .maybeSingle()
            .then(({ data: sub }) => {
              if (sub) setIsPlus(true);
            });
        });
    });
  }, [user, plusChecked]);

  const [nearbyEnabled, setNearbyEnabled] = useState(false);
  const [locating, setLocating] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [revealedDirections, setRevealedDirections] = useState<Set<string>>(new Set());
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(new Set());

  const handleToggleFavorite = useCallback(async (offerId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Bitte melde dich an, um Favoriten zu speichern.', { icon: '🔒' });
      return;
    }
    const result = await authToggleFavorite(offerId);
    if (result) {
      toast.success('Zu Favoriten hinzugefügt', { icon: '❤️', duration: 1800 });
    } else {
      toast.success('Aus Favoriten entfernt', { icon: '💔', duration: 1800 });
    }
  }, [user, authToggleFavorite]);

  const handleShare = (offer: Offer, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({ title: offer.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link kopiert!', { icon: '🔗', duration: 1800 });
    }
  };

  const handleNearbyToggle = () => {
    if (nearbyEnabled) {
      setNearbyEnabled(false);
      setUserLocation(null);
      return;
    }
    setLocating(true);
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocating(false);
          setNearbyEnabled(true);
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          toast.success('Standort gefunden!', { description: 'Zeige Epic Locations in deiner Nähe.', icon: '📍' });
        },
        () => {
          setLocating(false);
          toast.error('Standortzugriff verweigert', { description: 'Bitte erlaube Standortzugriff in deinen Einstellungen.' });
        },
        { timeout: 8000 }
      );
    } else {
      setLocating(false);
      toast.error('Geolocation nicht verfügbar in diesem Browser.');
    }
  };

  const handleRevealDirections = (offerId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isPlus) {
      setPlusModalHint('Schalte die genaue Wegbeschreibung zu dieser Epic Location frei.');
      setShowPlusModal(true);
      return;
    }
    setRevealedDirections((prev) => new Set([...prev, offerId]));
  };

  const toggleDetails = (offerId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedDetails((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) next.delete(offerId);
      else next.add(offerId);
      return next;
    });
  };

  const displayedOffers = nearbyEnabled
    ? [...offers].sort((a, b) => (a.distance_km ?? 999) - (b.distance_km ?? 999))
    : offers;

  const offersWithOnRequest = displayedOffers.filter((o) => o.on_request_offers.length > 0);

  const handleOnRequestInquiry = (item: string, offer: Offer, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setInquiryOffer(offer);
    setShowInquiryModal(true);
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: 'var(--background)' }}>
      {/* ── HEADER ── */}
      <div
        className="sticky top-0 z-40 px-4"
        style={{
          background: 'rgba(27,29,35,0.96)',
          borderBottom: '1px solid rgba(168,85,247,0.2)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}
      >
        <div className="flex items-center justify-between max-w-mobile mx-auto">
          <Link
            href="/"
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
            aria-label="Zurück zur Entdecken-Seite"
          >
            <ChevronLeft size={18} color="var(--card-foreground)" />
          </Link>

          <Image
            src="/assets/images/logo_grun_fur_app-1787922846254.png"
            alt="WINEWOOD Adventure"
            width={110}
            height={44}
            className="object-contain"
            priority
          />

          <button
            onClick={handleNearbyToggle}
            disabled={locating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-xs font-semibold transition-all duration-150 active:scale-95 disabled:opacity-60"
            style={{
              background: nearbyEnabled ? 'rgba(168,85,247,0.18)' : 'var(--muted)',
              color: nearbyEnabled ? '#a855f7' : 'var(--muted-foreground)',
              border: nearbyEnabled ? '1px solid rgba(168,85,247,0.4)' : '1px solid var(--border)',
            }}
            aria-label={nearbyEnabled ? 'Nähe-Filter deaktivieren' : 'Nähe-Filter aktivieren'}
          >
            {locating ? <Loader2 size={13} className="animate-spin" /> : nearbyEnabled ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
            {locating ? 'Suche...' : 'In der Nähe'}
          </button>
        </div>
      </div>

      {/* ── HERO BANNER ── */}
      <div className="px-4 pt-5 pb-4 max-w-mobile mx-auto">
        <div
          className="relative rounded-app overflow-hidden"
          style={{
            height: '150px',
            border: '1.5px solid rgba(168,85,247,0.45)',
            background: 'linear-gradient(135deg, rgba(60,10,90,0.92) 0%, rgba(30,5,55,0.97) 100%)',
          }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(168,85,247,0.22) 0%, transparent 65%)' }} />
          <div className="absolute top-4 right-8 w-1 h-1 rounded-full bg-purple-300 opacity-60" />
          <div className="absolute top-8 right-16 w-0.5 h-0.5 rounded-full bg-purple-200 opacity-40" />
          <div className="absolute bottom-6 right-10 w-1.5 h-1.5 rounded-full bg-purple-400 opacity-30" />

          <div className="relative z-10 flex flex-col justify-center h-full px-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(168,85,247,0.22)', border: '1.5px solid rgba(168,85,247,0.6)' }}>
                <Sparkles size={18} color="#a855f7" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white leading-tight">Epic Locations</h1>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Verborgene Orte. Unvergessliche Momente.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-pill" style={{ background: '#a855f7', color: '#fff' }}>
                {displayedOffers.length} Locations
              </span>
              {nearbyEnabled && (
                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-pill" style={{ background: 'rgba(168,85,247,0.2)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.35)' }}>
                  📍 Nach Nähe sortiert
                </span>
              )}
              {isPlus && (
                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-pill flex items-center gap-1" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                  <Crown size={10} /> WINEWOOD+
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── INFO BANNER ── */}
      <div className="px-4 mb-4 max-w-mobile mx-auto">
        <div className="flex items-start gap-3 p-3.5 rounded-app" style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)' }}>
          <Lock size={15} color="#a855f7" className="flex-shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
            Die genauen Wegbeschreibungen zu Epic Locations sind exklusiv für{' '}
            <span className="font-semibold" style={{ color: '#a855f7' }}>WINEWOOD+</span>{' '}
            Mitglieder. Entdecke verborgene Naturschätze in deiner Region.
          </p>
        </div>
      </div>

      {/* ── EPIC OFFER LIST ── */}
      <div className="px-4 max-w-mobile mx-auto">
        {displayedOffers.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'var(--muted)' }}>
              <Sparkles size={24} color="var(--muted-foreground)" />
            </div>
            <p className="text-sm font-semibold" style={{ color: 'var(--card-foreground)' }}>Keine Epic Locations</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Schau bald wieder vorbei!</p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {displayedOffers.map((offer) => {
              const isFav = favoriteIds?.has(offer.id) ?? false;
              const isRevealed = revealedDirections.has(offer.id);
              const isExpanded = expandedDetails.has(offer.id);

              return (
                <div
                  key={`epic-${offer.id}`}
                  className="rounded-app overflow-hidden"
                  style={{
                    background: 'var(--card)',
                    border: '1.5px solid rgba(168,85,247,0.5)',
                    boxShadow: '0 0 20px rgba(168,85,247,0.2), 0 4px 20px rgba(0,0,0,0.4)',
                  }}
                >
                  {/* Image */}
                  <Link href={`/offer-detail-page?id=${offer.id}`}>
                    <div className="relative" style={{ height: '220px' }}>
                      <SafeImage
                        src={offer.image_url}
                        alt={`${offer.title} in ${offer.city} — Epic Location`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 430px) 100vw, 430px"
                      />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)' }} />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(60,10,90,0.3) 0%, transparent 40%)' }} />

                      {/* Fav + Share */}
                      <div className="absolute top-3 left-3 flex gap-2">
                        <button
                          onClick={(e) => handleToggleFavorite(offer.id, e)}
                          className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
                          aria-label={isFav ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                        >
                          <Heart size={17} color={isFav ? '#ef4444' : 'white'} fill={isFav ? '#ef4444' : 'none'} />
                        </button>
                        <button
                          onClick={(e) => handleShare(offer, e)}
                          className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
                          aria-label={`${offer.title} teilen`}
                        >
                          <Share2 size={16} color="white" />
                        </button>
                      </div>

                      {/* Distance badge */}
                      {offer.distance_km !== undefined && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-pill" style={{ background: nearbyEnabled ? 'rgba(168,85,247,0.85)' : 'rgba(0,0,0,0.58)', backdropFilter: 'blur(8px)' }}>
                          <MapPin size={11} color={nearbyEnabled ? '#fff' : '#a855f7'} />
                          <span className="text-[11px] font-semibold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {offer.distance_km < 1 ? `${(offer.distance_km * 1000).toFixed(0)} m` : `${offer.distance_km.toFixed(1)} km`}
                          </span>
                        </div>
                      )}

                      {/* Epic badge */}
                      <div className="absolute bottom-3 left-3">
                        <div className="flex items-center gap-1 px-2.5 py-1 rounded-pill text-[11px] font-bold text-white" style={{ background: '#a855f7' }}>
                          <Sparkles size={11} /> Epic
                        </div>
                      </div>

                      {/* Free badge */}
                      {offer.price === 0 && (
                        <div className="absolute bottom-3 right-3">
                          <div className="px-2.5 py-1 rounded-pill text-[11px] font-bold" style={{ background: 'rgba(124,179,66,0.85)', color: '#fff' }}>Kostenlos</div>
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Card body */}
                  <div
                    className="p-3.5"
                    style={{
                      borderTop: '1px solid rgba(168,85,247,0.18)',
                      background: 'linear-gradient(to bottom, rgba(168,85,247,0.04) 0%, transparent 60%)',
                    }}
                  >
                    {/* Title + location */}
                    <Link href={`/offer-detail-page?id=${offer.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold leading-tight" style={{ color: 'var(--card-foreground)' }}>{offer.title}</h3>
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin size={11} color="var(--muted-foreground)" />
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{offer.city}</span>
                          </div>
                        </div>
                      </div>
                    </Link>

                    {/* Category pills */}
                    {offer.categories.length > 0 && (
                      <div className="flex gap-1.5 mb-3 flex-wrap">
                        {offer.categories.slice(0, 3).map((cat) => (
                          <span
                            key={`ecat-${offer.id}-${cat.id}`}
                            className="text-[10px] font-medium px-2 py-0.5 rounded-pill"
                            style={{ background: 'rgba(168,85,247,0.12)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.25)' }}
                          >
                            {cat.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Extended details toggle */}
                    <button
                      onClick={(e) => toggleDetails(offer.id, e)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg mb-2 transition-colors duration-150"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                    >
                      <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Details & Infos</span>
                      {isExpanded ? <ChevronUp size={14} color="var(--muted-foreground)" /> : <ChevronDown size={14} color="var(--muted-foreground)" />}
                    </button>

                    {isExpanded && (
                      <div className="mb-3 flex flex-col gap-2">
                        {/* Description */}
                        {offer.description && (
                          <p className="text-xs leading-relaxed px-1" style={{ color: 'rgba(255,255,255,0.65)' }}>
                            {offer.description}
                          </p>
                        )}

                        {/* Meta row */}
                        <div className="flex flex-wrap gap-2 mt-1">
                          {offer.opening_hours && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <Clock size={11} color="var(--muted-foreground)" />
                              <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{offer.opening_hours}</span>
                            </div>
                          )}
                          {offer.price > 0 && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <Tag size={11} color="var(--muted-foreground)" />
                              <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.6)' }}>ab {offer.price} €</span>
                            </div>
                          )}
                          {offer.website_url && (
                            <a
                              href={offer.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-colors duration-150"
                              style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', color: '#a855f7' }}
                            >
                              <ExternalLink size={11} />
                              <span className="text-[11px] font-medium">Website</span>
                            </a>
                          )}
                        </div>

                        {/* Address */}
                        {offer.address && (
                          <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                            <MapPin size={12} color="#a855f7" className="flex-shrink-0 mt-0.5" />
                            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{offer.address}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Directions section — Plus gated */}
                    {offer.epic_directions && (
                      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(168,85,247,0.25)', background: 'rgba(168,85,247,0.06)' }}>
                        <div className="flex items-center gap-2 px-3 py-2.5">
                          <Navigation size={13} color="#a855f7" />
                          <span className="text-xs font-semibold flex-1" style={{ color: '#a855f7' }}>Wegbeschreibung</span>
                          {!isPlus && !isRevealed && (
                            <div className="flex items-center gap-1">
                              <Crown size={11} color="var(--gold, #f59e0b)" />
                              <span className="text-[10px] font-semibold" style={{ color: 'var(--gold, #f59e0b)' }}>WINEWOOD+</span>
                            </div>
                          )}
                        </div>

                        {isPlus || isRevealed ? (
                          <div className="px-3 pb-3 text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                            {offer.epic_directions}
                          </div>
                        ) : (
                          <div className="px-3 pb-3">
                            <p className="text-xs leading-relaxed mb-2.5 select-none" style={{ color: 'rgba(255,255,255,0.3)', filter: 'blur(4px)', userSelect: 'none' }}>
                              {offer.epic_directions?.slice(0, 80)}...
                            </p>
                            <button
                              onClick={(e) => handleRevealDirections(offer.id, e)}
                              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all duration-150 active:scale-95"
                              style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.35)', color: '#a855f7' }}
                            >
                              <Lock size={12} />
                              Mit WINEWOOD+ freischalten
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CTA */}
                    <Link href={`/offer-detail-page?id=${offer.id}`}>
                      <button
                        className="w-full mt-3 py-2.5 rounded-lg text-xs font-bold transition-all duration-150 active:scale-95 flex items-center justify-center gap-2"
                        style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.35)', color: '#a855f7' }}
                      >
                        <Sparkles size={13} />
                        Details ansehen
                      </button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── ON-REQUEST OFFERS SECTION ── */}
      {offersWithOnRequest.length > 0 && (
        <div className="px-4 mt-8 max-w-mobile mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)' }}>
              <MessageSquare size={13} color="#a855f7" />
            </div>
            <h2 className="text-sm font-bold" style={{ color: 'var(--card-foreground)' }}>Auf Anfrage verfügbar</h2>
          </div>
          <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
            Diese besonderen Erlebnisse kannst du direkt an Epic Locations anfragen.
          </p>
          <div className="flex flex-col gap-3">
            {offersWithOnRequest.map((offer) =>
              offer.on_request_offers.map((item, idx) => (
                <button
                  key={`onreq-${offer.id}-${idx}`}
                  onClick={(e) => handleOnRequestInquiry(item, offer, e)}
                  className="flex items-center gap-3 p-3.5 rounded-app transition-all duration-150 active:scale-[0.98] text-left w-full"
                  style={{ background: 'var(--card)', border: '1px solid rgba(168,85,247,0.25)' }}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.3)' }}>
                    <Sparkles size={14} color="#a855f7" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold leading-snug" style={{ color: 'var(--card-foreground)' }}>{item}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>📍 {offer.title} · {offer.city}</p>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-pill flex-shrink-0" style={{ background: 'rgba(168,85,247,0.12)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.25)' }}>
                    <Send size={9} />
                    Anfragen
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <WinewoodPlusModal
        isOpen={showPlusModal}
        onClose={() => setShowPlusModal(false)}
        contextHint={plusModalHint}
      />

      <InquiryModal
        isOpen={showInquiryModal}
        onClose={() => { setShowInquiryModal(false); setInquiryOffer(null); }}
        onSent={() => { setShowInquiryModal(false); setInquiryOffer(null); }}
        offer={inquiryOffer ?? (offers[0] as Offer)}
        isLoggedIn={!!user}
        userName={epicUserDisplayName}
        userEmail={user?.email}
      />

      <BottomNav />
    </div>
  );
}
